"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import { CheckCircle2, Shield } from "lucide-react"

type Variant = "light" | "dark"
type SuccessMode = "redirect" | "inline"
type FormSource = "contact-page" | "home-cta"

interface ContactFormProps {
  variant: Variant
  successMode: SuccessMode
  formSource: FormSource
  heading?: string
  intro?: string
  privacyNote?: string
}

const WEBHOOK_URL =
  "https://app.hiveku.com/api/webhooks/trigger/2dbfb3fd-c9ex7ur3"

export default function ContactForm({
  variant,
  successMode,
  formSource,
  heading = "Send Us a Message",
  intro = "All inquiries are completely confidential.",
  privacyNote = "By submitting you agree to our privacy policy. We never share your information.",
}: ContactFormProps) {
  const router = useRouter()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const isDark = variant === "dark"

  const labelClass = isDark
    ? "block text-xs font-medium text-white/60 mb-1.5"
    : "block text-xs font-semibold tracking-[0.12em] uppercase text-foreground mb-1.5"

  const inputClass = isDark
    ? "w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[hsl(var(--sage))] transition-colors"
    : "w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--terracotta))]/25 focus:border-[hsl(var(--terracotta))] transition-all"

  const headingClass = isDark
    ? "font-heading text-xl font-semibold text-white mb-1"
    : "font-heading text-xl font-semibold text-[hsl(var(--sage-dark))] mb-1"

  const introClass = isDark
    ? "text-sm text-white/60 mb-5"
    : "text-sm text-muted-foreground mb-7"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, source: formSource }),
      })
    } catch (err) {
      console.error("Contact webhook failed:", err)
    }
    setSubmitting(false)

    if (successMode === "redirect") {
      router.push("/thank-you")
    } else {
      setSubmitted(true)
      setFormData({ name: "", email: "", phone: "", message: "" })
    }
  }

  // Inline success state (mirrors the original CTA section)
  if (successMode === "inline" && submitted) {
    return (
      <div className="text-center py-12">
        <CheckCircle2 className="w-14 h-14 text-[hsl(var(--sage))] mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-semibold text-white mb-3">
          Message Received
        </h3>
        <p className="text-white/70 text-sm leading-relaxed">
          Thank you for reaching out. A member of our team will be in touch
          within 1–2 business days.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <h2 className={headingClass}>{heading}</h2>
      <p className={introClass}>{intro}</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            {isDark ? "Full Name *" : "Your Name"}
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Jane Smith"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder={isDark ? "336-555-0100" : "(555) 555-5555"}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>
          {isDark ? "Email Address *" : "Email"}
        </label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder={isDark ? "you@example.com" : "you@email.com"}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>How Can We Help?</label>
        <textarea
          rows={isDark ? 4 : 5}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder={
            isDark
              ? "Tell us a little about what you're looking for..."
              : "Tell us a little about what you're going through (optional)"
          }
          className={`${inputClass} resize-none`}
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className={
          isDark
            ? "w-full inline-flex items-center justify-center rounded-full bg-[hsl(var(--terracotta))] text-white px-8 py-3.5 text-base font-semibold hover:bg-[hsl(var(--terracotta))]/90 disabled:opacity-60 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:translate-y-0"
            : "inline-flex items-center justify-center rounded-full bg-[hsl(var(--terracotta))] text-white px-8 py-3.5 text-sm font-semibold tracking-wide hover:bg-[hsl(var(--terracotta))]/90 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
        }
      >
        {submitting ? "Sending…" : "Send Message"}
      </button>

      {isDark ? (
        <p className="text-center text-xs text-white/40">{privacyNote}</p>
      ) : (
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-[hsl(var(--sage))] flex-shrink-0" />
          {privacyNote}
        </p>
      )}
    </form>
  )
}
